/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Amount } from "../../../../../types/amount";
import { PermitType } from "../../../../../utils/permitUtils";
import { useMachine } from "@xstate/react";
import { approveMachine } from "../../../../../utils/permitFSM";
import { GlobalStore } from "../../../../../store/global/GlobalStore";
import { useApproveViaPermits } from "../../../../../utils/abis/usePermits";
import { Hash } from "viem";
import { ConfirmInWallet } from "../ConfirmInWallet";
import { TryAgain } from "../TryAgain";
import { ProcessingTransaction } from "../ProcessingTransaction";
import { Approved } from "../Approved";
import { RevokeButton } from "../RevokeButton";
import { Text } from "../../../../../components/general/Text";
import { css } from "@emotion/react";
import { ApproveTokenTextContainer } from "./ApproveTokenTextContainer";
import { ApprovalStore } from "../../../../../store/approvals/ApprovalsStore";
import { useLogger } from "../../../../../utils/logger";
import { REVOKE_BEFORE_APPROVAL_REQUIRED } from "../../../../../config/consts";
import styled from "@emotion/styled";
import { FlexContainer } from "../../../../../containers/FlexContainer";
import { withIsVisible } from "../../../../../utils/reactUtils";
import { getTokenTicker, isAddressEqual } from "../../../../../utils/tokenUtils";
import { useTokenListByChain } from "../../../../../hooks/useTokenListByChain";

interface ApproveTokenProps {
  tokenAddress: Hash;
  tokenIcon: string;
  approvalTarget?: Hash;
  permitType: PermitType;
  needed: Amount;
  onStatusChange: (status: "DONE" | "CUSTOM_APPROVALS") => void;
  shouldStart: boolean;
}

export const ApprovalRow: React.FC<ApproveTokenProps> = ({
  needed,
  onStatusChange,
  permitType,
  approvalTarget,
  tokenAddress,
  tokenIcon,
  shouldStart = false,
}) => {
  const { logger } = useLogger();
  const tokenList = useTokenListByChain();
  const [currentState, send] = useMachine(approveMachine);
  const [txHash, setTxHash] = useState<Hash | undefined>(undefined);
  const [reApprovedAmount, setReapprovedAmount] = useState<Amount | undefined>(undefined);
  const [hasAnnouncedFinish, setHasAnnouncedFinish] = useState<boolean>(false);
  const hasRequestedApproval = useRef(false);
  const { approve } = useApproveViaPermits(permitType, approvalTarget);
  const theme = GlobalStore.useState(s => s.theme);
  const isRetrying =
    currentState.matches({ APPROVE: "RETRY_APPROVE" }) ||
    currentState.matches({ REVOKE: "RETRY_REVOKE" });

  useEffect(() => {
    if (shouldStart && currentState.matches({ APPROVE: "PENDING_APPROVAL" })) {
      send({ type: "ON_APPROVAL_START" });
    }
    if (shouldStart && currentState.matches({ REVOKE: "PENDING_REVOKE" })) {
      send({ type: "ON_REVOKE_START" });
    }
  }, [shouldStart, currentState, send]);
  useEffect(() => {
    logger.log(`Current State: ${tokenAddress}:${JSON.stringify(currentState.value)}`);
  }, [logger, currentState, tokenAddress]);

  useEffect(() => {
    if (currentState.matches("HAS_EXISTING_APPROVAL")) {
      const approval = ApprovalStore.getRawState().currentAllowances.find(allowance =>
        isAddressEqual(allowance.tokenAddress, tokenAddress)
      )?.allowance;
      if (approval) {
        if (approval.value.gt(0)) {
          send({ type: "ON_HAS_EXISTING_APPROVAL" });
        } else {
          send({ type: "ON_INSUFFICIENT_APPROVAL" });
        }
      }
    }
  }, [currentState, tokenAddress, send]);
  useEffect(() => {
    if (currentState.matches("IS_APPROVAL_SUFFICIENT")) {
      const approval = ApprovalStore.getRawState().initialAllowances.find(allowance =>
        isAddressEqual(allowance.tokenAddress, tokenAddress)
      )?.allowance;
      const needed = GlobalStore.getRawState().takerTokens.find(e =>
        isAddressEqual(e.tokenAddress, tokenAddress)
      )?.amount;
      if (
        approval &&
        needed &&
        approval.value.gt(0) &&
        needed.value.gt(0) &&
        approval.value.gte(needed.value)
      ) {
        send({ type: "ON_SUFFICIENT_APPROVAL" });
      } else {
        send({ type: "ON_INSUFFICIENT_APPROVAL" });
      }
    }
  }, [currentState, tokenAddress, send]);

  const updateStoreApproval = useCallback(
    (newAmount: Amount) => {
      ApprovalStore.update(s => {
        s.currentAllowances = s.currentAllowances.map(allowance => {
          if (isAddressEqual(allowance.tokenAddress, tokenAddress)) {
            return {
              ...allowance,
              allowance: newAmount,
            };
          } else {
            return allowance;
          }
        });
      });
    },
    [tokenAddress]
  );

  const handleRevoke = useCallback(() => {
    const doAsync = async () => {
      setReapprovedAmount(undefined);
      try {
        const SHOULD_REVOKE = true;
        await approve(
          tokenAddress,
          txHash => {
            send({ type: "ON_REVOKE_CONFIRM" });
            setTxHash(txHash);
          },
          newAmount => {
            hasRequestedApproval.current = false;
            send({ type: "ON_TX_RECEIPT" });
            updateStoreApproval(newAmount);
          },
          SHOULD_REVOKE
        );
      } catch (e) {
        hasRequestedApproval.current = false;
        logger.log(`Rejected: ${JSON.stringify(e)}`);
        send({ type: "ON_REVOKE_REJECT" });
      }
    };
    if (!hasRequestedApproval.current) {
      hasRequestedApproval.current = true;
      void doAsync();
    }
  }, [logger, updateStoreApproval, approve, tokenAddress, send]);

  const handleApprove = useCallback(() => {
    const doAsync = async () => {
      try {
        await approve(
          tokenAddress,
          txHash => {
            send({ type: "ON_CONFIRM" });
            setTxHash(txHash);
          },
          newAmount => {
            hasRequestedApproval.current = false;
            logger.log(`Reapproved amount: ${newAmount.value.toString()}`);
            setReapprovedAmount(newAmount);
            updateStoreApproval(newAmount);
            send({ type: "ON_TX_RECIEPT" });
          }
        );
      } catch (e) {
        hasRequestedApproval.current = false;
        logger.log(`Rejected ${JSON.stringify(e)}`);
        send({ type: "ON_APPROVAL_REJECT" });
        send({ type: "ON_TX_FAIL" });
      }
    };
    if (!hasRequestedApproval.current) {
      hasRequestedApproval.current = true;
      void doAsync();
    }
  }, [approve, tokenAddress, send, logger, updateStoreApproval]);

  useEffect(() => {
    if (currentState.matches("IS_REAPPROVED_AMOUNT_SUFFICIENT") && reApprovedAmount) {
      logger.log(`reApprovedAmount: ${reApprovedAmount.toString()}`);
      if (reApprovedAmount.value.gte(needed.value)) {
        send({ type: "ON_SUFFICIENT_AMOUNT" });
        setReapprovedAmount(undefined); // Clean reapproved amount for future states
      } else {
        const reApprovedAmountString = reApprovedAmount.value.toString() ?? "";
        logger.log(`Approved for ${reApprovedAmountString} but needed ${needed.value.toString()}`);
        send({ type: "ON_INSUFFICIENT_APPROVAL" });
      }
    }
  }, [logger, currentState, needed.value, reApprovedAmount, send]);

  useEffect(() => {
    if (currentState.matches({ APPROVE: "TX_CONFIRMED" })) {
      send({ type: "ON_APPROVED" });
    }
  }, [currentState, needed.value, send]);

  useEffect(() => {
    if (currentState.matches({ APPROVE: "CONFIRM_IN_WALLET" })) {
      handleApprove();
    }
  }, [currentState, handleApprove]);

  useEffect(() => {
    if (currentState.matches({ REVOKE: "CONFIRM_IN_WALLET" })) {
      handleRevoke();
    }
  }, [currentState, handleRevoke]);

  useEffect(() => {
    if (currentState.matches("DONE") && !hasAnnouncedFinish) {
      setHasAnnouncedFinish(true);
      onStatusChange("DONE");
    }
  }, [currentState, hasAnnouncedFinish, onStatusChange]);

  useEffect(() => {
    if (currentState.matches("CUSTOM_APPROVALS")) {
      logger.log(`User set custom approvals for ${tokenAddress}, restarting process`);
      onStatusChange("CUSTOM_APPROVALS");
      send({ type: "ON_RESTART" });
    }
  }, [currentState, logger, onStatusChange, send, tokenAddress]);

  return (
    <ApprovalRowContainer isActive={shouldStart && !hasAnnouncedFinish} key={tokenAddress}>
      <div
        css={css`
          display: flex;
          padding: 0px 14px;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
        `}
      >
        <FlexContainer
          gap={6}
          css={css`
            padding: 4px 0;
          `}
        >
          <img
            src={tokenIcon}
            alt={`${tokenAddress} symbol`}
            css={css`
              ${shouldStart ? "" : "opacity: 0.4"}
            `}
          />
          <Text
            textStyle={"Title"}
            size={"Medium"}
            shouldScale={false}
            color={shouldStart ? theme.font.primary.default : theme.font.quaternary.default}
          >
            {getTokenTicker(tokenAddress, tokenList)}
          </Text>
        </FlexContainer>
        <ApproveTokenTextContainer>
          <Text
            textStyle={"Paragraph"}
            size={"Large"}
            shouldScale={false}
            color={theme.font.secondary.default}
            isVisible={currentState.matches("IS_REVOKE_FIRST_TOKEN")}
          >
            Approval too low
          </Text>
          <Text
            textStyle={"Paragraph"}
            size={"Large"}
            shouldScale={false}
            color={theme.font.secondary.default}
            isVisible={currentState.matches({ REVOKE: "REVOKE_TX_CONFRIMED" })}
          >
            Reapproval required
          </Text>
          <div style={{ display: "flex" }}>
            <PendingApproval
              isVisible={currentState.matches({ APPROVE: "PENDING_APPROVAL" })}
              isActive={shouldStart}
            />
            <ConfirmInWallet
              isVisible={
                currentState.matches({ APPROVE: "CONFIRM_IN_WALLET" }) ||
                currentState.matches({ REVOKE: "CONFIRM_IN_WALLET" })
              }
              confirmOrApproveText={"Confirm"}
            />
            <RejectedText isVisible={isRetrying} isActive={shouldStart} />
            <ProcessingTransaction
              isVisible={
                currentState.matches({ APPROVE: "WAITING_FOR_TX" }) ||
                currentState.matches({ REVOKE: "WAITING_FOR_TX" })
              }
              tx={txHash?.toString() ?? ""}
            />
            <Approved isVisible={currentState.matches("DONE")} tx={txHash?.toString() ?? ""} />
            <RevokeButton
              isVisible={currentState.matches({ REVOKE: "PENDING_REVOKE" })}
              onClick={() => {
                send({ type: "ON_REVOKE_START" });
              }}
            />
          </div>
        </ApproveTokenTextContainer>
      </div>
      <TryAgain
        isVisible={isRetrying}
        text={
          currentState.matches({ APPROVE: "RETRY_APPROVE" }) ? "Retry approval" : "Retry revoke"
        }
        onClick={() => {
          if (currentState.matches({ APPROVE: "RETRY_APPROVE" })) send({ type: "ON_RETRY" });
          if (currentState.matches({ REVOKE: "RETRY_REVOKE" })) send({ type: "ON_RETRY_REVOKE" });
        }}
      />
      <TryAgain
        isVisible={currentState.matches({ REVOKE: "REVOKE_TX_CONFRIMED" })}
        text={"Reapprove"}
        onClick={() => {
          send({ type: "ON_REVOKED" });
        }}
      />
      <TryAgain
        isVisible={currentState.matches("IS_REVOKE_FIRST_TOKEN")}
        text={
          REVOKE_BEFORE_APPROVAL_REQUIRED.some(e => isAddressEqual(e, tokenAddress))
            ? "Revoke"
            : "Increase Spend Limit"
        }
        onClick={() => {
          REVOKE_BEFORE_APPROVAL_REQUIRED.some(e => isAddressEqual(e, tokenAddress))
            ? send({ type: "ON_IS_REVOKE_FIRST" })
            : send({ type: "ON_IS_NOT_REVOKE_FIRST" });
        }}
      />
    </ApprovalRowContainer>
  );
};

const RejectedText: React.FC<{ isVisible?: boolean; isActive: boolean }> = withIsVisible(
  ({ isActive }) => {
    const theme = GlobalStore.useState(s => s.theme);

    return (
      <Text
        textStyle={"Paragraph"}
        size={"Large"}
        color={isActive ? theme.font.secondary.default : theme.font.quaternary.default}
      >
        Rejected
      </Text>
    );
  }
);

const PendingApproval: React.FC<{ isVisible?: boolean; isActive: boolean }> = withIsVisible(
  ({ isActive }) => {
    const theme = GlobalStore.useState(s => s.theme);

    return (
      <Text
        textStyle={"Paragraph"}
        size={"Large"}
        color={isActive ? theme.font.primary.default : theme.font.quaternary.default}
      >
        Approval required
      </Text>
    );
  }
);

const ApprovalRowContainer = styled.div<{ isActive: boolean }>`
  display: flex;
  padding: 14px;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  align-self: stretch;
  background: ${props =>
    props.isActive
      ? props.theme.surface.main.secondary.default
      : props.theme.surface.alt.secondary.default};
  border-radius: 24px;
`;
